import { routeList } from '@routes/routeList';
import { slugifySegment } from '@utilities/catalog/slug';

export const DEFAULT_CATALOG_PAGE_SIZE = 20;

export type CatalogPlpQuery = {
  pageNumber?: number | string;
  pageSize?: number | string;
  shapeCode?: number | string;
  shapeName?: string;
  includePaging?: boolean;
};

const toSearch = (query?: CatalogPlpQuery) => {
  const params = new URLSearchParams();

  const pageNumber = Number(query?.pageNumber);
  const pageSize = Number(query?.pageSize);
  const includePaging = Boolean(query?.includePaging);

  if (includePaging) {
    params.set('pageNumber', String(pageNumber > 0 ? pageNumber : 1));
    params.set(
      'pageSize',
      String(
        query?.pageSize && Number.isFinite(pageSize)
          ? pageSize
          : DEFAULT_CATALOG_PAGE_SIZE,
      ),
    );
  } else {
    if (pageNumber > 1) {
      params.set('pageNumber', String(pageNumber));
    }

    if (
      query?.pageSize &&
      Number.isFinite(pageSize) &&
      pageSize !== DEFAULT_CATALOG_PAGE_SIZE
    ) {
      params.set('pageSize', String(query.pageSize));
    }
  }

  if (query?.shapeCode) {
    params.set('shapeCode', String(query.shapeCode));
  }

  if (query?.shapeName) {
    params.set('shapeName', String(query.shapeName));
  }

  const search = params.toString();
  return search ? `?${search}` : '';
};

export const buildOtcPlpPath = (
  productListName: string,
  query?: CatalogPlpQuery,
) =>
  `${routeList.otcMedicineProductsList}/${encodeURIComponent(
    productListName,
  )}${toSearch(query)}`;

export const buildSectionPlpPath = (
  basePath: string,
  productListName: string,
  query?: CatalogPlpQuery,
  level3Name?: string | null,
) => {
  const level3 = level3Name
    ? `/${encodeURIComponent(level3Name)}`
    : '';
  return `${basePath}/${encodeURIComponent(productListName)}${level3}${toSearch(query)}`;
};

export const buildCatalogPdpPath = (
  plpBasePath: string,
  productListName: string,
  productId: string,
  productName?: string | null,
) => {
  const slug = slugifySegment(productName);
  const path = `${plpBasePath}/${encodeURIComponent(
    productListName,
  )}/${encodeURIComponent(productId)}`;
  return slug ? `${path}/${encodeURIComponent(slug)}` : path;
};

export type PdpPathQuery = {
  fromShopping?: boolean;
  brandName?: string;
  categoryCodeLevel3?: string;
};

const toPdpSearch = (query?: PdpPathQuery) => {
  const params = new URLSearchParams();

  if (query?.fromShopping) {
    params.set('fromShopping', 'true');
  }

  if (query?.brandName) {
    params.set('brandName', query.brandName);
  }

  if (query?.categoryCodeLevel3) {
    params.set('categoryCodeLevel3', query.categoryCodeLevel3);
  }

  const search = params.toString();
  return search ? `?${search}` : '';
};

export const buildSupplementPdpPath = (
  productId: string,
  productName?: string | null,
  query?: PdpPathQuery,
) => {
  const slug = slugifySegment(productName);
  const path = `${routeList.supplementProduct}/${encodeURIComponent(productId)}`;
  const withSlug = slug ? `${path}/${encodeURIComponent(slug)}` : path;
  return `${withSlug}${toPdpSearch(query)}`;
};

export const buildPdpPath = (
  productId: string,
  productName?: string | null,
  query?: PdpPathQuery,
) => {
  const slug = slugifySegment(productName);
  const path = slug
    ? `${routeList.searchProductPage}/${encodeURIComponent(productId)}/${encodeURIComponent(slug)}`
    : `${routeList.searchProductPage}/${encodeURIComponent(productId)}`;

  return `${path}${toPdpSearch(query)}`;
};

export const getProductId = (product?: {
  irc?: string | null;
  genericCode?: string | null;
  productId?: string | null;
}) => product?.irc || product?.genericCode || product?.productId || '';

export const withResolvedSearch = (
  destination: string,
  resolvedUrl?: string,
) => {
  const incomingIndex = resolvedUrl?.indexOf('?') ?? -1;
  if (incomingIndex < 0) return destination;

  const [path, existingSearch = ''] = destination.split('?');
  const merged = new URLSearchParams(existingSearch);
  const incoming = new URLSearchParams(resolvedUrl.slice(incomingIndex + 1));

  incoming.forEach((value, key) => {
    if (!merged.has(key)) {
      merged.set(key, value);
    }
  });

  const search = merged.toString();
  return search ? `${path}?${search}` : path;
};
